import { Component, Input, Output, EventEmitter, OnChanges, OnDestroy, SimpleChanges, HostListener, ElementRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject, of } from 'rxjs';
import { catchError, map, take, takeUntil } from 'rxjs/operators';

import { PermitV2Service } from '../../../services/permits.abstract.service';
import { PermitStatusService } from '../../../services/permit-status.service';
import { MessageService } from '../../../../global/services/message/message.abstract.service';
import { AdminPermitDetail, AdminPermitListItem, AllowedStatusTransition, PopulatedPermitTypeRef, UpdateStatusPayload } from '../../../models/permit.model';
import { I18nString } from '../../../models/permit-category.model';
import { PermitStatusErrorCode, PermitStatusType, PSD_MANAGED_STATUS_TYPES } from '../../../constants/permit.constants';
import { PermitStatusChangeDialogComponent } from '../../dialogs/permit-status-change-dialog/permit-status-change-dialog.component';

/** Why the picker has nothing to offer — drives which explanation the agent sees. */
type LockedReason = 'psd_managed' | 'terminal';

@Component({
    selector: 'app-permit-status-select',
    templateUrl: './permit-status-select.component.html',
    styleUrls: ['./permit-status-select.component.scss'],
})
export class PermitStatusSelectComponent implements OnChanges, OnDestroy {
    /**
     * A list row or a full detail. A detail carries `allowedStatusTransitions`; a list row does not
     * (the field is per-permit state and deliberately absent from the list response), so in that
     * case the options are fetched on first open.
     */
    @Input() permit: AdminPermitListItem | AdminPermitDetail;

    @Input() disabled = false;

    @Output() statusChanged = new EventEmitter<AdminPermitListItem>();

    /**
     * Raised when the local copy is known to be stale — the backend refused a transition the picker
     * offered, which means the permit moved elsewhere (PSD sync, or another agent). Hosts should
     * refetch: `allowedStatusTransitions` is the source of truth and ours is out of date.
     */
    @Output() refreshRequested = new EventEmitter<void>();

    isOpen = false;
    isLoading = false;
    transitionsLoading = false;

    dropdownPosition: { top: number; left: number };

    /** Detail fetched lazily in list mode, to resolve transitions and the connected/terminal flags. */
    private fetchedDetail: AdminPermitDetail | null = null;

    private destroy$ = new Subject<void>();

    constructor(
        private elementRef: ElementRef,
        private permitService: PermitV2Service,
        // Display-only: used solely to look up a target's `terminal` flag for the confirm dialog's
        // warning. The picker's options come from `allowedStatusTransitions` and must never be
        // sourced or filtered from here — the org status list is not gated.
        private statusService: PermitStatusService,
        private messageService: MessageService,
        private dialog: MatDialog,
        private translate: TranslateService,
    ) {}

    statusName(name: I18nString | string): string {
        if (!name) return '';
        if (typeof name === 'string') return name;
        const lang = this.translate.currentLang;
        return name[lang] || name.fr || name.en || '';
    }

    ngOnChanges(changes: SimpleChanges): void {
        // A replaced permit brings its own (fresh) transitions; anything we fetched for the previous
        // object no longer applies.
        if (changes.permit) {
            this.fetchedDetail = null;
            this.isOpen = false;
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    // ─── Allowed transitions ──────────────────────────────────────────────────

    /** The options to render, or null while still unknown (list mode, before the first open). */
    get transitions(): AllowedStatusTransition[] | null {
        const embedded = (this.permit as AdminPermitDetail)?.allowedStatusTransitions;
        if (Array.isArray(embedded)) return embedded;
        return this.fetchedDetail?.allowedStatusTransitions ?? null;
    }

    get transitionsKnown(): boolean {
        return this.transitions !== null;
    }

    /** Known to have no options: an expected state, rendered as an explanation rather than an empty menu. */
    get isLocked(): boolean {
        return this.transitions?.length === 0;
    }

    /** Only disable up-front when we already know there is nothing to pick. */
    get isBadgeDisabled(): boolean {
        return this.disabled || this.isLocked;
    }

    get lockedReason(): LockedReason {
        const currentType = this.permit?.state?.type;
        // On a connected type, a decision status is PSD's to change — say so even if the status is
        // also flagged terminal, since "go change it in PSD" is the actionable half.
        if (this.isConnectedType && currentType && PSD_MANAGED_STATUS_TYPES.has(currentType)) {
            return 'psd_managed';
        }
        return 'terminal';
    }

    get lockedMessageKey(): string {
        return `permit.manage.status_select.locked_${this.lockedReason}`;
    }

    /**
     * PSD models "waiting on the applicant" as two internal states and only one of them can go back
     * to review, so a connected permit at PENDING_INFORMATION may legitimately not offer
     * UNDER_REVIEW. Agents read that as a bug; name it instead.
     */
    get showPendingInfoHint(): boolean {
        if (!this.isConnectedType || this.permit?.state?.type !== PermitStatusType.PENDING_INFORMATION) return false;
        const { transitions } = this;
        if (!transitions?.length) return false;
        return !transitions.some((t) => t.type === PermitStatusType.UNDER_REVIEW);
    }

    private get permitType(): PopulatedPermitTypeRef | undefined {
        return this.fetchedDetail?.permitType || this.permit?.permitType;
    }

    /** Both flags are required: the backend gates on `connected` AND `externalId`. */
    private get isConnectedType(): boolean {
        const type = this.permitType;
        return type?.connected === true && !!type?.externalId;
    }

    private loadTransitions(): void {
        if (this.transitionsKnown || this.transitionsLoading || !this.permit?._id) return;
        this.transitionsLoading = true;
        this.permitService
            .getPermitById(this.permit._id)
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe({
                next: (detail) => {
                    this.fetchedDetail = detail;
                    this.transitionsLoading = false;
                },
                error: () => {
                    this.transitionsLoading = false;
                    this.isOpen = false;
                    this.messageService.error(this.translate.instant('permit.manage.common.error'));
                },
            });
    }

    // ─── Dropdown ─────────────────────────────────────────────────────────────

    toggleDropdown(event: Event): void {
        if (this.isLoading || this.isBadgeDisabled) return;
        event.stopPropagation();
        this.isOpen = !this.isOpen;
        if (this.isOpen) {
            this.calculateDropdownPosition();
            this.loadTransitions();
        }
    }

    calculateDropdownPosition(): void {
        const badgeElement = this.elementRef.nativeElement.querySelector('.status-badge');
        if (badgeElement) {
            const rect = badgeElement.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const viewportWidth = window.innerWidth;

            const titleHeight = 25;
            const optionHeight = 32;
            const containerPadding = 32;
            // Unknown yet (list mode): assume a single row, the panel repositions on scroll/resize anyway.
            const optionCount = this.transitions?.length || 1;
            const estimatedDropdownHeight = Math.min(220, optionCount * optionHeight) + titleHeight + containerPadding;
            const dropdownWidth = 200;

            let top = rect.bottom + 8;
            let { left } = rect;

            if (top + estimatedDropdownHeight > viewportHeight) {
                top = rect.top - estimatedDropdownHeight - 8;
            }

            if (left + dropdownWidth > viewportWidth) {
                left = rect.right - dropdownWidth;
            }

            if (left < 8) {
                left = 8;
            }

            this.dropdownPosition = { top, left };
        }
    }

    // ─── Status change ────────────────────────────────────────────────────────

    onStatusSelected(target: AllowedStatusTransition): void {
        this.isOpen = false;

        const needsDialog = [PermitStatusType.APPROVED, PermitStatusType.REFUSED, PermitStatusType.CONDITIONALLY_APPROVED].includes(target.type as PermitStatusType);

        if (!needsDialog) {
            this.patchStatus({ status: target._id }, target);
            return;
        }

        this.resolveTerminalFlag(target)
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe((terminal) => this.openReasonDialog(target, terminal));
    }

    /**
     * `terminal` drives the dialog's "this status is final" warning, and `allowedStatusTransitions`
     * does not carry it — so look it up in the org status list. `terminal` is an ordinary per-status
     * config flag unrelated to the PSD gate, and losing the warning before an irreversible change is
     * worse than one request. Only runs when a confirm dialog is actually needed (APPROVED /
     * REFUSED / CONDITIONALLY_APPROVED), never on render, and prefers the flag off the transition
     * itself so this resolves to a no-op once the payload carries it.
     */
    private resolveTerminalFlag(target: AllowedStatusTransition): Observable<boolean | undefined> {
        const embedded = (target as AllowedStatusTransition & { terminal?: boolean }).terminal;
        if (typeof embedded === 'boolean') return of(embedded);

        return this.statusService.getAllStatuses().pipe(
            map((statuses) => (statuses || []).find((status) => status._id === target._id)?.terminal),
            // Unknown beats blocked: show the dialog without the warning rather than swallow the change.
            catchError(() => of(undefined)),
        );
    }

    private openReasonDialog(target: AllowedStatusTransition, terminal: boolean | undefined): void {
        this.dialog
            .open(PermitStatusChangeDialogComponent, { width: '480px', data: { targetStatus: { ...target, terminal } } })
            .afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe((result) => {
                if (result) {
                    const payload: UpdateStatusPayload = { status: target._id, reason: result.reason };
                    if (result.conditions !== undefined) {
                        payload.conditions = result.conditions;
                    }
                    this.patchStatus(payload, target);
                }
            });
    }

    private patchStatus(payload: UpdateStatusPayload, target: AllowedStatusTransition): void {
        const previous = this.permit.state;
        // optimistic chip update
        this.permit.state = {
            _id: target._id,
            name: this.statusName(target.name),
            color: target.color,
            type: target.type,
        };
        this.isLoading = true;
        this.permitService
            .updateStatus(this.permit._id, payload)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.isLoading = false;
                    // The move changes which transitions are available next; drop what we fetched so
                    // the following open re-resolves them.
                    this.fetchedDetail = null;
                    this.statusChanged.emit(this.permit);
                },
                error: (err) => {
                    this.isLoading = false;
                    this.permit.state = previous; // revert
                    this.handleStatusError(err);
                },
            });
    }

    /** Backend `message` is already translated by its i18n filter, so it is shown as-is. */
    private handleStatusError(err: any): void {
        const { code, message } = err?.error ?? {};

        switch (code) {
            case PermitStatusErrorCode.PSD_MANAGED:
            case PermitStatusErrorCode.TERMINAL:
                // Refused because our view of the permit is behind: re-resolve rather than just warn.
                this.fetchedDetail = null;
                this.refreshRequested.emit();
                this.messageService.warning(message || this.translate.instant('permit.manage.common.error'));
                break;
            default:
                this.messageService.error(message || this.translate.instant('permit.manage.common.error'));
        }
    }

    getChipBackgroundColor(color: string, opacity = 0.3): string {
        if (!color) return 'rgba(0,0,0,0.05)';
        const hex = color.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        return `rgba(${r},${g},${b},${opacity})`;
    }

    @HostListener('document:click', ['$event'])
    clickOutside(event: Event): void {
        if (!this.elementRef.nativeElement.contains(event.target)) {
            this.isOpen = false;
        }
    }

    @HostListener('window:scroll', [])
    onWindowScroll(): void {
        if (this.isOpen) {
            this.calculateDropdownPosition();
        }
    }

    @HostListener('window:resize', [])
    onWindowResize(): void {
        if (this.isOpen) {
            this.calculateDropdownPosition();
        }
    }
}
